import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, FileTextIcon, YoutubeIcon, BookOpenIcon, FileDownIcon, ExternalLinkIcon } from 'lucide-react';
import type { SearchResult, SearchResultType } from './WebSearchModal';
const TYPE_CONFIG: Record<SearchResultType, {
  icon: React.ElementType;
  className: string;
  label: string;
}> = {
  article: {
    icon: FileTextIcon,
    className: 'text-[#2563EB]',
    label: 'Article'
  },
  video: {
    icon: YoutubeIcon,
    className: 'text-rose-500',
    label: 'Video'
  },
  paper: {
    icon: BookOpenIcon,
    className: 'text-purple-500',
    label: 'Paper'
  },
  pdf: {
    icon: FileDownIcon,
    className: 'text-amber-600',
    label: 'PDF'
  }
};
function getDomain(url: string): string {
  try {
    return new URL(url).hostname.replace('www.', '');
  } catch {
    return '';
  }
}
function getFaviconUrl(url: string): string {
  const domain = getDomain(url);
  return domain ? `https://www.google.com/s2/favicons?domain=${domain}&sz=32` : '';
}
export interface SearchResultItemProps {
  result: SearchResult;
  index: number;
  isSelected: boolean;
  isAlreadyImported: boolean;
  onToggle: (id: string) => void;
}
export function SearchResultItem({
  result,
  index,
  isSelected,
  isAlreadyImported,
  onToggle
}: SearchResultItemProps) {
  const faviconUrl = getFaviconUrl(result.url);
  const handleVisit = (e: React.MouseEvent) => {
    e.stopPropagation();
    window.open(result.url, '_blank', 'noopener,noreferrer');
  };
  return <motion.div initial={{
    opacity: 0,
    y: 4
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    delay: index * 0.03
  }} onClick={() => {
    if (!isAlreadyImported) onToggle(result.id);
  }} className={`group flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-100 ${isAlreadyImported ? 'bg-[#00B75F]/[0.03] cursor-default' : isSelected ? 'bg-gray-500/[0.04] cursor-pointer' : 'hover:bg-surface-elevated cursor-pointer'}`} data-id="element-1173">
      {/* Checkbox */}
      <div className="flex-shrink-0" data-id="element-1174">
        {isAlreadyImported ? <div className="w-4 h-4 rounded bg-[#00B75F] flex items-center justify-center" data-id="element-1175">
            <CheckIcon size={11} strokeWidth={3} className="text-white" data-id="element-1176" />
          </div> : <div className={`w-4 h-4 rounded flex items-center justify-center border transition-colors ${isSelected ? 'bg-gray-700 border-gray-700 text-white' : 'border-gray-300 bg-surface group-hover:border-gray-400'}`} data-id="element-1177">
            {isSelected && <CheckIcon size={11} strokeWidth={3} data-id="element-1178" />}
          </div>}
      </div>

      {/* Favicon */}
      {faviconUrl ? <img src={faviconUrl} alt="" className="w-4 h-4 rounded-sm flex-shrink-0 object-contain" loading="lazy" data-id="element-1179" /> : <div className="w-4 h-4 rounded-sm bg-gray-100 flex-shrink-0" data-id="element-1180" />}

      {/* Title + URL + Type */}
      <div className="flex-1 min-w-0" data-id="element-1181">
        <h3 className={`text-[13px] font-medium leading-tight truncate ${isAlreadyImported ? 'text-text-secondary' : 'text-text-primary transition-colors'}`} data-id="element-1182">
          {result.title}
        </h3>
        <p className="text-[11px] text-text-tertiary truncate mt-0.5 flex items-center gap-1" data-id="element-1183">
          {result.type && TYPE_CONFIG[result.type] && (() => {
          const {
            icon: Icon,
            className,
            label
          } = TYPE_CONFIG[result.type!];
          return <Icon size={11} className={`flex-shrink-0 ${className}`} aria-label={label} data-id="element-1184" />;
        })()}
          <span className="truncate" data-id="element-1185">{result.url || result.domain}</span>
        </p>
      </div>

      {/* Visit button — visible on hover */}
      <button onClick={handleVisit} className="flex-shrink-0 p-1 rounded opacity-0 group-hover:opacity-100 text-text-tertiary hover:text-text-primary hover:bg-surface-elevated transition-all" aria-label="Open link in new tab" data-id="element-1186">
        <ExternalLinkIcon size={13} data-id="element-1187" />
      </button>

      {/* Imported badge */}
      {isAlreadyImported && <span className="px-1.5 py-0.5 bg-[#00B75F]/10 rounded text-[9px] font-semibold text-[#00B75F] flex-shrink-0" data-id="element-1188">
          Imported
        </span>}
    </motion.div>;
}